import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardImg,
  CardText,
  CardTitle,
  Col,
  Container,
  Input,
  InputGroup,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useNavigate } from "react-router-dom";
import MediaForm from "./MediaForm";
import MediaService from "../../../services/KangarooServices/MediaServices";
import toast from "react-hot-toast";
import DeleteModal from "../../../Components/Common/DeleteModal";
import { Triangle } from "react-loader-spinner";
import blog from "../../../assets/kangaroo/Australian.png";
import MediaDetail from "../component/MediaDetail";
import Masonry from "react-masonry-css";
import placeholderImg from "../../../assets/kangaroo/Australian.png";
import { imageBaseApi } from "../../../utilis/imageHelper.js"; // adjust path as needed
import MediaGroupService from "../../../services/KangarooServices/MediaGroupService.js";
import Select from "react-select";

const MediaList = () => {
  const [data, setData] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSelectMode, setIsSelectMode] = useState(false); // New: Bulk select mode
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [pageSize] = useState(12);
  const [filters, setFilters] = useState({
    search: "",
    mediaGroupId: null,
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const [mediaGroup, setMediaGroup] = useState([]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters]);

  const fetchGroup = async (pageNumber = 1, size = 1000) => {
    try {
      setLoading(true);
      const response = await MediaGroupService.getList({
        pageNumber,
        pageSize: size,
      });
      setMediaGroup(response?.items);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchGroup();
  }, []);
  // Fetch media
  const fetchMedia = async (page = 1) => {
    setIsLoading(true);
    try {
      const res = await MediaService.getList({
        ...debouncedFilters,
        page,
        pageSize,
      });
      setData(res.items || []);
      setTotalRows(res.totalCount || 0);
    } catch (err) {
      toast.error("Failed to load media");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia(currentPage);
  }, [currentPage, debouncedFilters]);

  const totalPages = Math.ceil(totalRows / pageSize);

  // Toggle modals
  const toggleModal = (item) => {
    setSelectedItem(item);
    setIsOpen(!isOpen);
  };
  const toggleDetailModal = (item = null) => {
    if (isSelectMode) return; // Disable view in select mode
    setSelectedItem(item);
    setIsModalOpen(!isModalOpen);
  };
  const toggleDeleteModal = () => setIsOpenDelete(!isOpenDelete);

  // Selection handlers
  const toggleSelectItem = (item) => {
    setSelectedItems((prev) =>
      prev.find((i) => i.id === item.id)
        ? prev.filter((i) => i.id !== item.id)
        : [...prev, item]
    );
  };

  const clearSelection = () => {
    setSelectedItems([]);
    setIsSelectMode(false);
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;

    try {
      await Promise.all(
        selectedItems.map((item) => MediaService.delete(item.id))
      );
      toast.success(`${selectedItems.length} item(s) deleted`);
      clearSelection();
      fetchMedia(currentPage);
    } catch (err) {
      toast.error("Some items could not be deleted");
    } finally {
      toggleDeleteModal();
    }
  };

  const mediaGroupOptions = mediaGroup.map((group) => ({
    label: group.name,
    value: group.id,
  }));
  const handleMediaGroupChange = (selectedOption) => {
    setFilters((prev) => ({
      ...prev,
      mediaGroupId: selectedOption ? selectedOption.value : null,
    }));
  };

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb title="Media Gallery" pageTitle="Media" />

        {/* Header */}
        <Row className="align-items-center mb-4">
          <Col md={4}>
            <div className="d-flex align-items-center gap-3">
              <Input
                placeholder="Search media..."
                value={filters.search}
                onChange={(e) => setFilters({ search: e.target.value })}
                className="w-100 shadow-sm"
              />
            </div>
          </Col>
          <Col xs={7} lg={4} className=" ">
            <Select
              options={mediaGroupOptions}
              value={mediaGroupOptions.find(
                (option) => option.value === filters.mediaGroupId
              )}
              onChange={handleMediaGroupChange}
              placeholder="Select Media Group"
              isClearable
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </Col>
          <Col lg={4} className="text-end">
            {!isSelectMode && (
              <div className="d-inline-flex gap-2">
                <Button
                  color="outline-primary"
                  onClick={() => setIsSelectMode(true)}
                >
                  <i className="bx bx-check-square me-1" />
                  Bulk
                </Button>
                <Button color="success" onClick={toggleModal}>
                  <i className="bx bx-plus me-1" />
                  Upload Media
                </Button>
              </div>
            )}
          </Col>
        </Row>

        {/* Loading & Empty State */}
        {isLoading ? (
          <div className="text-center d-flex justify-content-center align-content-center align-items-center py-5">
            <Triangle height="100" width="100" color="#556ee6" />
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-5">
            <i className="bx bx-image fs-1 text-muted d-block mb-3" />
            <h5>No media found</h5>
            <Button color="primary" onClick={toggleModal}>
              Upload your first file
            </Button>
          </div>
        ) : (
          <>
            {/* Optional: Select all on page */}
            {isSelectMode && (
              <div className="mb-3 p-3 bg-white rounded d-flex align-items-center justify-content-between">
                <label className="d-flex align-items-center mb-0 cursor-pointer">
                  <Input
                    type="checkbox"
                    checked={
                      selectedItems.length === data.length && data.length > 0
                    }
                    onChange={() =>
                      selectedItems.length === data.length
                        ? setSelectedItems([])
                        : setSelectedItems([...data])
                    }
                    className="me-2"
                  />
                  <span>Select all {data.length} items</span>
                </label>

                <div className="d-inline-flex align-items-center gap-3">
                  <Badge color="primary" pill className="fs-14 px-3 py-2">
                    {selectedItems.length} selected
                  </Badge>
                  <Button
                    color="danger"
                    size="sm"
                    disabled={selectedItems.length === 0}
                    onClick={toggleDeleteModal}
                  >
                    <i className="bx bx-trash " />
                  </Button>
                  <Button color="light" size="sm" onClick={clearSelection}>
                    <i class="bx bx-x"></i>
                  </Button>
                </div>
              </div>
            )}

            {/* Masonry Grid */}
            <Row className="g-3">
              {data?.map((item) => {
                const isSelected = selectedItems?.some((i) => i.id === item.id);
                const isImage = item.mimeType.startsWith("image/");

                return (
                  <Col
                    key={item.id}
                    lg={2}
                    md={3}
                    className="position-relative"
                  >
                    <Card
                      className={`h-100 border-0 rounded-3 overflow-hidden cursor-pointer shadow-sm ${
                        isSelected ? "border-primary" : "border-transparent"
                      }`}
                      onClick={() => {
                        if (isSelectMode) toggleSelectItem(item);
                        else toggleDetailModal(item);
                      }}
                      style={{
                        transition: "all 0.2s ease-in-out",
                        transform: isSelected ? "scale(0.97)" : "scale(1)",
                        border: isSelected
                          ? "3px solid #556ee6"
                          : "3px solid transparent",
                      }}
                    >
                      {/* Selection Checkbox */}
                      {isSelectMode && (
                        <div className="position-absolute top-2 start-2 z-index-10">
                          <Input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectItem(item)}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              width: "20px",
                              height: "20px",
                              accentColor: "#556ee6",
                            }}
                          />
                        </div>
                      )}

                      {/* Media Preview */}
                      {isImage ? (
                        <CardImg
                          top
                          src={`${imageBaseApi}${item?.url}`}
                          alt={item?.fileName}
                          style={{
                            objectFit: "contain",
                            height: "220px",
                            width: "100%",
                          }}
                          loading="lazy"
                          className="transition-all"
                        />
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-center bg-light text-muted"
                          style={{
                            height: "220px",
                            fontSize: "50px",
                            transition: "all 0.2s",
                          }}
                        >
                          <i className="bx bx-file-blank" />
                        </div>
                      )}

                      {/* Overlay Info */}
                      <div
                        className="position-absolute bottom-0 start-0 end-0 p-2 text-white"
                        style={{
                          background:
                            "linear-gradient(180deg, transparent, rgba(0,0,0,0.7))",
                        }}
                      >
                        <div className="text-truncate fw-semibold">
                          {item.fileName}
                        </div>
                        <div className="d-flex justify-content-between align-content-center align-items-center">
                          <small className="d-block">
                            {new Date(item.uploadedAt).toLocaleDateString()}
                          </small>
                          <button
                            className="btn btn-warning btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleModal(item);
                            }}
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                        </div>
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-5">
                <Pagination>
                  <PaginationItem disabled={currentPage === 1}>
                    <PaginationLink
                      previous
                      onClick={() => setCurrentPage((p) => p - 1)}
                    />
                  </PaginationItem>
                  {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                    const page = currentPage <= 4 ? i + 1 : i + currentPage - 4;
                    if (page > totalPages) return null;
                    return (
                      <PaginationItem key={page} active={currentPage === page}>
                        <PaginationLink onClick={() => setCurrentPage(page)}>
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  <PaginationItem disabled={currentPage === totalPages}>
                    <PaginationLink
                      next
                      onClick={() => setCurrentPage((p) => p + 1)}
                    />
                  </PaginationItem>
                </Pagination>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <MediaForm
        isOpen={isOpen}
        toggle={toggleModal}
        onSuccess={() => fetchMedia(1)}
        fetchMedia={fetchMedia}
        selectedItem={selectedItem}
      />
      <MediaDetail
        isOpen={isModalOpen}
        toggle={() => toggleDetailModal()}
        selectedItem={selectedItem}
      />

      <DeleteModal
        show={isOpenDelete}
        onCloseClick={toggleDeleteModal}
        onDeleteClick={handleBulkDelete}
        title={`Delete ${selectedItems.length} item${
          selectedItems.length > 1 ? "s" : ""
        }?`}
        body="This action cannot be undone."
      />
    </Container>
  );
};

export default MediaList;
